<template>
  <div class="cursor-page">
    <el-card shadow="never" class="card">
      <template #header>
        <div class="card-head">
          <span class="card-title">🖱 上传鼠标指针</span>
          <span class="card-hint">整个文件夹 / 一个 zip / <b>单个文件</b>都行 · 支持 .ani（动态）/ .cur / .ico / png / gif / svg · <b>不会弹「是否将文件上传到此站点」那个提示了</b></span>
        </div>
      </template>

      <el-form label-width="110px">
        <el-form-item label="指针文件夹">
          <el-input v-model="folderName" placeholder="在线上的文件夹名，中文英文都行，例如 像素指针" maxlength="64" style="max-width: 320px" />
          <span class="tip">不填就用你选的文件夹名</span>
        </el-form-item>

        <el-form-item label="选择文件">
          <input ref="dirInput" type="file" webkitdirectory directory multiple class="hidden-input" @change="onPickDir" />
          <input ref="zipInput" type="file" accept=".zip,application/zip" class="hidden-input" @change="onPickZip" />
          <input ref="fileInput" type="file" :accept="FILE_ACCEPT" multiple class="hidden-input" @change="onPickFiles" />
          <el-button @click="pickFolder" :disabled="uploading" :data-picker="canPickDir ? 'fs' : 'input'">📁 选择文件夹</el-button>
          <el-button @click="zipInput?.click()" :disabled="uploading">🗜 选择 zip</el-button>
          <el-button @click="fileInput?.click()" :disabled="uploading">📄 选择文件</el-button>
          <span class="tip" v-if="picked.length">{{ picked.length }} 个文件，共 {{ formatSize(pickedBytes) }}</span>
        </el-form-item>

        <el-form-item label="拖进来">
          <div class="drop-zone" :class="{ over: dragOver }" @dragenter.prevent="dragOver = true"
            @dragover.prevent="dragOver = true" @dragleave.prevent="dragOver = false" @drop.prevent="onDrop">
            <template v-if="dragOver">松手就上传 ✅</template>
            <template v-else>
              把<b>文件夹 / zip / 指针文件</b>拖到这里<br />
              <span class="drop-tip">拖进来也行，跟点「选择文件夹」一个效果</span>
            </template>
          </div>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" :loading="uploading" :disabled="!picked.length" @click="doUpload">开始上传</el-button>
          <el-button @click="clearPicked" :disabled="uploading">清空选择</el-button>
        </el-form-item>
      </el-form>

      <el-alert v-if="uploadMsg" :title="uploadMsg" :type="uploadOk ? 'success' : 'error'" :closable="false" show-icon />
    </el-card>

    <el-card shadow="never" class="card">
      <template #header>
        <div class="card-head">
          <span class="card-title">🖱 已上传的指针</span>
          <el-button size="small" @click="loadList" :loading="loading">刷新</el-button>
        </div>
      </template>

      <p class="picked-line">
        已在随机池：<b>{{ chosen.length }}</b> 个
        <template v-if="chosen.length">（{{ chosen.join('、') }}）</template>
        <span class="tip">—— 每次刷新随机出现一个</span>
      </p>

      <el-empty v-if="!items.length" description="还没有上传过指针" />

      <el-collapse v-else v-model="opened" class="cursor-collapse" @change="onCollapseChange">
        <el-collapse-item v-for="it in items" :key="it.folder" :name="it.folder">
          <template #title>
            <span class="folder-title" :class="{ active: pickedInFolder(it.folder) }">
              📁 {{ it.folder }}
              <el-tag size="small" type="info">{{ it.files.length }} 个指针</el-tag>
              <el-tag size="small" type="info">{{ formatSize(it.bytes) }}</el-tag>
              <el-tag v-if="pickedInFolder(it.folder)" size="small" type="success">已选 {{ pickedCount(it.folder) }}</el-tag>
            </span>
          </template>

          <div class="folder-body">
            <div class="cur-list">
              <div v-for="f in it.files" :key="it.folder + '/' + f.rel" class="cur-row">
                <el-checkbox v-model="chosen" :label="it.folder + '/' + f.rel" class="cur-check">
                  <span class="cur-name">{{ f.rel }}</span>
                </el-checkbox>
                <span class="cur-size">{{ formatSize(f.size) }}</span>
                <canvas
                  :ref="(el) => setPreviewEl(it.folder + '/' + f.rel, el)"
                  class="mini-preview"
                  :style="miniStyle(it.folder + '/' + f.rel)"></canvas>
              </div>
            </div>
            <div class="folder-ops">
              <el-button size="small" @click="useFirst(it)">全选本夹</el-button>
              <el-button size="small" @click="clearFolder(it)">取消本夹</el-button>
              <el-button v-if="canDelete()" size="small" type="danger" plain @click="doRemove(it.folder)">删除</el-button>
            </div>
          </div>
        </el-collapse-item>
      </el-collapse>

      <div class="save-bar">
        <span class="label">指针大小</span>
        <el-slider v-model="cursorSize" :min="16" :max="96" :step="2" style="width: 200px" />
        <span class="size-text">{{ cursorSize }} px</span>
        <el-switch v-model="enabled" :active-value="1" :inactive-value="0" active-text="开启" />
        <el-button type="primary" :loading="saving" @click="doSave">保存</el-button>
      </div>
      <p class="tip-line">
        右边的小图就是实际大小的预览（跟着上面的滑杆变）；<b>保存</b>后前台会立刻更新，不用刷新页面；
        <b>开启</b>后全站鼠标都换成它（后台页面除外）。
      </p>
    </el-card>

    <teleport to="body">
      <div v-if="uploading" class="cursor-mask" @click.stop.prevent @mousedown.stop.prevent @contextmenu.prevent>
        <div class="cursor-mask-card">
          <div class="cursor-spinner"></div>
          <p class="cursor-mask-title">正在上传指针，请稍候…</p>
          <el-progress :percentage="percent" :stroke-width="10" :status="percent >= 100 ? 'success' : ''" />
          <p class="cursor-mask-detail">{{ uploadedText }}</p>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import axios from 'axios';
import { parseCursorFile, contentBox } from '../../../util/aniCursor';
import { broadcastSettingsChanged } from '../../../util/settingsBus';
import { canDelete } from '../../../util/permission';

const dirInput = ref(null);
const zipInput = ref(null);
const fileInput = ref(null);

const items = ref([]);
const opened = ref([]);          // 默认全部收起
const loading = ref(false);
const saving = ref(false);
const uploading = ref(false);
const percent = ref(0);
const uploadedBytes = ref(0);
const totalBytes = ref(0);
const uploadMsg = ref('');
const uploadOk = ref(true);

const folderName = ref('');
const chosen = ref([]);          // 勾选的指针："文件夹/文件名"
const cursorSize = ref(32);
const enabled = ref(0);
const previewVer = ref(0);       // 预览数据变了就 +1，好让模板重新算尺寸

const picked = ref([]);
const pickedBytes = computed(() => picked.value.reduce((s, f) => s + (f.size || 0), 0));
// 单个文件也能传（一次可以多选几个）；zip 交给 zip 那个按钮，这里只放行指针文件
const FILE_ACCEPT = '.ani,.cur,.ico,.png,.gif,.svg,.jpg,.jpeg,.webp,.bmp';

// 每个文件一个小 canvas + 自己的一份帧数据
const previewEls = new Map();
const previewData = new Map();
let rafId = 0;
let loadingPreviews = false;

const formatSize = (n) => {
  const v = Number(n) || 0;
  if (v < 1024) return v + ' B';
  if (v < 1024 * 1024) return (v / 1024).toFixed(1) + ' KB';
  return (v / 1024 / 1024).toFixed(2) + ' MB';
};

const uploadedText = computed(() => {
  if (!uploading.value) return '';
  if (percent.value >= 100) return '文件已发送完毕，服务器正在落盘…';
  if (totalBytes.value > 0) return '已上传 ' + formatSize(uploadedBytes.value) + ' / ' + formatSize(totalBytes.value);
  return '正在发送数据…';
});

const pickedInFolder = (folder) => chosen.value.some((v) => v.indexOf(folder + '/') === 0);
const pickedCount = (folder) => chosen.value.filter((v) => v.indexOf(folder + '/') === 0).length;

const setPreviewEl = (key, el) => {
  if (el) previewEls.set(key, el);
  else previewEls.delete(key);
};

/** 小预览的显示尺寸：按后台设的像素数，跟真正上站一模一样 */
const miniStyle = (key) => {
  previewVer.value;   // 依赖一下，数据或尺寸变了要重算
  const d = previewData.get(key);
  if (!d) return { width: '26px', height: '26px', opacity: 0.15 };
  const s = cursorSize.value / Math.max(d.box.w, d.box.h);
  return {
    width: Math.max(8, Math.round(d.box.w * s)) + 'px',
    height: Math.max(8, Math.round(d.box.h * s)) + 'px',
    opacity: 1
  };
};

const drawOne = (key) => {
  const el = previewEls.get(key);
  const d = previewData.get(key);
  if (!el || !d) return;
  const ctx = el.getContext('2d');
  ctx.clearRect(0, 0, el.width, el.height);
  ctx.drawImage(d.frames[d.idx % d.frames.length], -d.box.x, -d.box.y);
};

const loop = (now) => {
  previewData.forEach((d, key) => {
    if (!previewEls.has(key)) return;
    const rate = d.rates[d.idx % d.rates.length] || 6;
    const dur = Math.max(20, (rate * 1000) / 60);
    if (now - d.at >= dur) {
      d.at = now - ((now - d.at) % dur);
      d.idx = (d.idx + 1) % d.frames.length;
      drawOne(key);
    }
  });
  rafId = requestAnimationFrame(loop);
};

/** 展开哪个文件夹，就把里面的指针逐个解析出来放小预览（懒加载，展开才做） */
const loadFolderPreviews = async (folder) => {
  if (loadingPreviews) return;
  const it = items.value.find((x) => x.folder === folder);
  if (!it) return;
  loadingPreviews = true;
  try {
    for (const f of it.files) {
      const key = it.folder + '/' + f.rel;
      if (previewData.has(key) || !previewEls.has(key)) continue;
      try {
        const url = '/cursor/' + key.split('/').map(encodeURIComponent).join('/');
        const res = await fetch(url);
        if (!res.ok) continue;
        const buf = await res.arrayBuffer();
        const parsed = await parseCursorFile(buf, f.rel);
        const box = contentBox(parsed.frames[0]);
        const el = previewEls.get(key);
        if (el) { el.width = box.w; el.height = box.h; }
        previewData.set(key, {
          frames: parsed.frames,
          rates: parsed.rates && parsed.rates.length ? parsed.rates : [60000],
          box: box,
          idx: 0,
          at: 0
        });
        previewVer.value += 1;
        drawOne(key);
      } catch (e) {
        console.warn('[指针预览] ' + key + ' 解析失败:', e.message);
      }
      await new Promise((r) => setTimeout(r, 16));   // 别把主线程占死
    }
  } finally {
    loadingPreviews = false;
  }
};

const onCollapseChange = (val) => {
  const arr = Array.isArray(val) ? val : (val ? [val] : []);
  arr.forEach((folder) => loadFolderPreviews(folder));
};

const loadList = async () => {
  loading.value = true;
  try {
    const res = await axios.get('/adminapi/cursor/list');
    const d = res.data || {};
    if (d.code !== 200) { ElMessage.error(d.message || '读取失败'); return; }
    items.value = d.items || [];
    const cur = d.current || {};
    let picks = [];
    try { picks = cur.picks ? (Array.isArray(cur.picks) ? cur.picks : JSON.parse(cur.picks)) : []; } catch (e) { picks = []; }
    if (!picks.length && cur.folder && cur.main) picks = [cur.folder + '/' + cur.main];
    // 只留服务器上真的还在的：文件夹删掉之后，勾选项在列表里没有复选框，
    // 主人怎么点都取消不掉，一保存就报「这个指针文件不存在」—— 这里直接剔掉
    const alive = [];
    (d.items || []).forEach((it) => (it.files || []).forEach((f) => alive.push(it.folder + '/' + f.rel)));
    const kept = picks.filter((u) => alive.indexOf(u) >= 0);
    const dropped = picks.filter((u) => alive.indexOf(u) < 0);
    if (dropped.length) ElMessage.warning('有 ' + dropped.length + ' 个原来勾选的指针已经不在服务器上了（' + dropped.join('、') + '），已自动去掉');
    chosen.value = kept;
    cursorSize.value = Number(cur.size) || 32;
    enabled.value = Number(cur.enabled) === 1 ? 1 : 0;
    // 一个都没勾过：默认勾第一个文件夹里的第一个指针
    if (!chosen.value.length && items.value.length) {
      const first = items.value.find((x) => x.files.length);
      if (first) chosen.value = [first.folder + '/' + first.files[0].rel];
    }
  } catch (e) {
    ElMessage.error('网络错误：' + (e.response?.data?.message || e.message));
  } finally {
    loading.value = false;
  }
};

const useFirst = (it) => {
  if (!it.files.length) { ElMessage.warning('这个文件夹里没有可用的指针文件'); return; }
  const all = it.files.map((f) => it.folder + '/' + f.rel);
  const keep = chosen.value.filter((v) => v.indexOf(it.folder + '/') !== 0);
  chosen.value = keep.concat(all);
};

const clearFolder = (it) => {
  chosen.value = chosen.value.filter((v) => v.indexOf(it.folder + '/') !== 0);
};

/* ---------------- 选文件夹：优先走 File System Access API ----------------
 * 老写法 <input type="file" webkitdirectory> 会让 Chrome 弹
 * 「是否将 N 个文件上传到此站点？请仅在信任该网站时才执行此操作」——
 * 那是浏览器的安全确认，网页端关不掉。showDirectoryPicker() 同样是选文件夹，
 * 但走的是系统目录选择器，不弹那句话。只有浏览器不支持时才回退到老 input。 */
const canPickDir = typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';

const readDirHandle = async (dirHandle, prefix, out) => {
  for await (const handle of dirHandle.values()) {
    if (handle.kind === 'file') {
      const file = await handle.getFile();
      try { file.__rel = prefix + handle.name; } catch (e) {}
      out.push(file);
    } else if (handle.kind === 'directory') {
      await readDirHandle(handle, prefix + handle.name + '/', out);
    }
  }
};

const pickFolder = async () => {
  if (!canPickDir) { dirInput.value?.click(); return; }   // Firefox / Safari 回退
  let handle = null;
  try {
    handle = await window.showDirectoryPicker({ mode: 'read' });
  } catch (e) {
    if (e && (e.name === 'AbortError' || e.name === 'NotAllowedError')) return;  // 主人自己取消了
    ElMessage.warning('系统文件夹选择器打不开：' + (e && (e.message || e.name) || '未知原因') + '，把文件夹拖进来也一样');
    return;
  }
  if (!handle) return;
  const files = [];
  try {
    await readDirHandle(handle, handle.name + '/', files);
  } catch (e) {
    ElMessage.error('读文件夹失败：' + (e.message || e.name));
    return;
  }
  if (!files.length) { ElMessage.warning('这个文件夹里没读到文件'); return; }
  picked.value = files;
  if (!folderName.value) folderName.value = String(handle.name || '').replace(/[\\/:*?"<>|]/g, '_').slice(0, 64);
  uploadMsg.value = '';
  ElMessage.success('读到 ' + files.length + ' 个文件，点「开始上传」就行');
};

const onPickDir = (e) => {
  const files = Array.from(e.target.files || []);
  picked.value = files;
  const first = files[0];
  if (first && first.webkitRelativePath && !folderName.value) {
    folderName.value = first.webkitRelativePath.split('/')[0].replace(/[\\/:*?"<>|]/g, '_').slice(0, 64);
  }
  uploadMsg.value = '';
};

const onPickZip = (e) => {
  picked.value = Array.from(e.target.files || []);
  const z = picked.value[0];
  if (z && !folderName.value) folderName.value = z.name.replace(/\.zip$/i, '').replace(/[\\/:*?"<>|]/g, '_').slice(0, 64);
  uploadMsg.value = '';
};

/* ---------------- 拖拽上传 ----------------
 * 为什么加这个：点「选择文件夹」时 Chrome 一定会弹
 * 「是否将 N 个文件上传到此站点？请仅在信任该网站时才执行此操作」——
 * 那是浏览器的安全确认，网页关不掉。而拖拽走的是 DataTransfer，
 * 同样能把整个文件夹（含子目录）读出来，且不弹提示。 */
const dragOver = ref(false);

const readAllEntries = (reader) => new Promise((resolve) => reader.readEntries(resolve, () => resolve([])));

const walkEntry = async (entry, prefix, out) => {
  if (!entry) return;
  if (entry.isFile) {
    const file = await new Promise((resolve) => entry.file(resolve, () => resolve(null)));
    if (file) {
      try { file.__rel = prefix + file.name; } catch (e) {}   // 相对路径，交给后端还原目录结构
      out.push(file);
    }
    return;
  }
  if (entry.isDirectory) {
    const reader = entry.createReader();
    let batch = await readAllEntries(reader);
    while (batch && batch.length) {
      for (const it of batch) await walkEntry(it, prefix + entry.name + '/', out);
      batch = await readAllEntries(reader);
    }
  }
};

const onDrop = async (e) => {
  dragOver.value = false;
  const dt = e.dataTransfer;
  if (!dt) return;
  const files = [];
  const items = dt.items ? Array.from(dt.items) : [];
  const entries = [];
  items.forEach((it) => {
    const en = it.webkitGetAsEntry ? it.webkitGetAsEntry() : null;
    if (en) entries.push(en);
  });
  if (entries.length) {
    for (const en of entries) await walkEntry(en, '', files);
  } else {
    Array.from(dt.files || []).forEach((f) => files.push(f));
  }
  if (!files.length) { ElMessage.warning('没读到文件，换个方式试试'); return; }
  picked.value = files;
  // 文件夹名默认用相对路径的第一段（拖进来的文件夹名）
  const rel = files[0].webkitRelativePath || files[0].__rel || '';
  if (!folderName.value && rel && rel.indexOf('/') > 0) {
    folderName.value = rel.split('/')[0].replace(/[\\/:*?"<>|]/g, '_').slice(0, 64);
  } else if (!folderName.value) {
    folderName.value = String(files[0].name || '').replace(/\.[^.]+$/, '').replace(/[\\/:*?"<>|]/g, '_').slice(0, 64);
  }
  uploadMsg.value = '';
  ElMessage.success('读到 ' + files.length + ' 个文件，点「开始上传」就行');
};

const onPickFiles = (e) => {
  const files = Array.from(e.target.files || []);
  picked.value = files;
  const first = files[0];
  // 单文件：文件夹名默认用「文件名去掉后缀」，主人想改再改
  if (first && !folderName.value) {
    folderName.value = String(first.name || '').replace(/\.[^.]+$/, '').replace(/[\\/:*?"<>|]/g, '_').slice(0, 64);
  }
  uploadMsg.value = '';
};

const clearPicked = () => {
  picked.value = [];
  if (dirInput.value) dirInput.value.value = '';
  if (zipInput.value) zipInput.value.value = '';
  if (fileInput.value) fileInput.value.value = '';
};

const doUpload = async () => {
  if (!picked.value.length) return;
  const fd = new FormData();
  const folder = (folderName.value || '').trim();
  if (folder) fd.append('folder', folder);
  for (const f of picked.value) fd.append('files', f, f.name);
  for (const f of picked.value) fd.append('paths', f.webkitRelativePath || f.__rel || f.name);

  uploading.value = true;
  percent.value = 0;
  uploadedBytes.value = 0;
  totalBytes.value = pickedBytes.value;
  uploadMsg.value = '';
  try {
    const res = await axios.post('/adminapi/cursor/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
      onUploadProgress: (ev) => {
        const loaded = Number(ev.loaded) || 0;
        const total = Number(ev.total) || 0;
        uploadedBytes.value = loaded;
        if (total > 0) { totalBytes.value = total; percent.value = Math.min(99, Math.floor((loaded / total) * 100)); }
        else { percent.value = Math.min(99, percent.value + 1); }
      }
    });
    percent.value = 100;
    await new Promise((r) => setTimeout(r, 300));
    const d = res.data || {};
    uploadOk.value = d.code === 200;
    uploadMsg.value = d.message || (uploadOk.value ? '上传完成' : '上传失败');
    if (uploadOk.value) {
      clearPicked();
      folderName.value = '';       // 传完就把指针文件夹名输入框清空，接着传下一个
      await loadList();
      if (d.folder) {
        const it = items.value.find((x) => x.folder === d.folder);
        if (it) {
          if (!chosen.value.length && it.files.length) chosen.value = [it.folder + '/' + it.files[0].rel];
          opened.value = [it.folder];
          loadFolderPreviews(it.folder);
        }
      }
    }
  } catch (e) {
    uploadOk.value = false;
    const status = e.response?.status;
    uploadMsg.value = '上传失败：' + (status === 413 ? '文件太大，服务器拒收（nginx 上限已调到 300MB）' : (e.response?.data?.message || e.message));
  } finally {
    uploading.value = false;
  }
};

const doSave = async () => {
  if (enabled.value === 1 && !chosen.value.length) {
    ElMessage.warning('先勾选至少一个指针再开启');
    return;
  }
  saving.value = true;
  try {
    const res = await axios.post('/adminapi/cursor/save', {
      picks: chosen.value, size: cursorSize.value, enabled: enabled.value
    });
    const d = res.data || {};
    if (d.code === 200) {
      ElMessage.success(d.message || '已保存');
      broadcastSettingsChanged();   // 通知正在浏览前台的标签页立刻更新
      await loadList();
      if (d.missing && d.missing.length) ElMessage.warning('顺手清掉了 ' + d.missing.length + ' 个已经不在服务器上的勾选');
    } else {
      ElMessage.error(d.message || '保存失败');
    }
  } catch (e) {
    ElMessage.error('保存失败：' + (e.response?.data?.message || e.message));
  } finally {
    saving.value = false;
  }
};

const doRemove = async (name) => {
  try {
    await ElMessageBox.confirm('确定删除指针文件夹「' + name + '」吗？', '提示', { type: 'warning' });
  } catch (e) { return; }
  try {
    const res = await axios.post('/adminapi/cursor/delete', { folder: name });
    const d = res.data || {};
    if (d.code === 200) {
      ElMessage.success(d.message || '已删除');
      chosen.value = chosen.value.filter((v) => v.indexOf(name + '/') !== 0);
      await loadList();
    }
    else { ElMessage.error(d.message || '删除失败'); }
  } catch (e) {
    ElMessage.error('删除失败：' + (e.response?.data?.message || e.message));
  }
};

onMounted(() => {
  loadList();
  rafId = requestAnimationFrame(loop);
});

onBeforeUnmount(() => {
  if (rafId) cancelAnimationFrame(rafId);
});
</script>

<style scoped>
.cursor-page { padding: 4px; }
.card { margin-bottom: 16px; }
.card-head { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; }
.card-title { font-size: 15px; font-weight: 600; }
.card-hint, .tip { font-size: 12px; color: #909399; margin-left: 6px; }
.hidden-input { display: none; }

.picked-line { margin: 0 0 10px; font-size: 13px; color: #606266; }

.cursor-collapse { border-top: none; }
.folder-title { display: flex; align-items: center; gap: 8px; font-weight: 600; }
.folder-title.active { color: #409eff; }
.folder-body { display: flex; justify-content: space-between; gap: 16px; padding: 4px 0 4px 6px; }
.cur-list { flex: 1 1 auto; display: flex; flex-direction: column; gap: 4px; max-height: 320px; overflow: auto; }
.cur-row { display: flex; align-items: center; gap: 10px; padding: 2px 0; }
.cur-check { margin-right: 0; }
.cur-name { font-size: 13px; }
.cur-size { font-size: 12px; color: #909399; min-width: 62px; text-align: right; }
.mini-preview {
  image-rendering: pixelated;
  background-color: #f5f7fa;
  background-image: linear-gradient(45deg, #e9edf2 25%, transparent 25%), linear-gradient(-45deg, #e9edf2 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e9edf2 75%), linear-gradient(-45deg, transparent 75%, #e9edf2 75%);
  background-size: 10px 10px; background-position: 0 0, 0 5px, 5px -5px, -5px 0;
  border-radius: 4px;
  min-width: 26px; min-height: 26px;
}
.folder-ops { display: flex; flex-direction: column; gap: 8px; }

.save-bar { margin-top: 16px; display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.save-bar .label { font-size: 13px; color: #606266; }
.save-bar .size-text { font-size: 13px; color: #409eff; min-width: 50px; }
.tip-line { margin: 10px 0 0; font-size: 12px; color: #909399; }

.drop-zone { width: 100%; max-width: 460px; padding: 18px 14px; border: 2px dashed #dcdfe6; border-radius: 10px; text-align: center; color: #606266; font-size: 13px; line-height: 1.9; background: #fafafa; transition: all .15s; }
.drop-zone.over { border-color: #409eff; background: #ecf5ff; color: #409eff; }
.drop-tip { font-size: 12px; color: #909399; }

.cursor-mask { position: fixed; inset: 0; z-index: 3000; display: flex; align-items: center; justify-content: center; background: rgba(0, 0, 0, .45); cursor: wait; user-select: none; }
.cursor-mask-card { width: 330px; padding: 26px 24px 18px; border-radius: 12px; background: #fff; box-shadow: 0 12px 32px rgba(0, 0, 0, .18); text-align: center; }
.cursor-mask-title { margin: 0 0 16px; font-size: 15px; font-weight: 500; color: #303133; }
.cursor-mask-detail { margin: 10px 0 0; font-size: 13px; color: #606266; min-height: 18px; }
.cursor-spinner { width: 46px; height: 46px; margin: 0 auto 16px; border: 4px solid #e4e7ed; border-top-color: #409eff; border-radius: 50%; animation: cursor-spin .8s linear infinite; }
@keyframes cursor-spin { to { transform: rotate(360deg); } }
</style>
